/**
 * Getting-started guide cards and mini-wizard step graphs for workspace tenant admins.
 * Option outcomes are either the next step id or a navigate path.
 */

export type HomeGuideCardId =
  | "externalData"
  | "excelCsv"
  | "dataPrep"
  | "internalAnalysis"
  | "guideDocs"
  | "anomaly"
  | "insight"
  | "chatWidget"
  | "chatAdaptorReport"
  | "dashboard";

export type HomeGuideOption = {
  id: string;
  /** i18n key under home.guide.options.* */
  labelKey: string;
  /** When true and VPN is disabled, option is shown disabled. */
  requiresVpn?: boolean;
  nextStep?: string;
  navigate?: string;
};

export type HomeGuideStep = {
  id: string;
  /** i18n key under home.guide.steps.* */
  questionKey: string;
  options: HomeGuideOption[];
};

export type HomeGuideCard = {
  id: HomeGuideCardId;
  /** Material outlined icon name */
  icon: string;
  tone?: "primary" | "info" | "success" | "warning" | "error" | "secondary";
  titleKey: string;
  descriptionKey: string;
  steps: HomeGuideStep[];
};

export const HOME_GUIDE_CARDS: HomeGuideCard[] = [
  {
    id: "externalData",
    icon: "dns",
    tone: "primary",
    titleKey: "home.guide.cards.externalData.title",
    descriptionKey: "home.guide.cards.externalData.description",
    steps: [
      {
        id: "access",
        questionKey: "home.guide.steps.externalAccess",
        options: [
          { id: "direct", labelKey: "home.guide.options.directAccess", nextStep: "adaptorType" },
          { id: "vpn", labelKey: "home.guide.options.vpnAccess", requiresVpn: true, nextStep: "vpnReady" },
          { id: "unsure", labelKey: "home.guide.options.unsure", navigate: "/docs/getting-started" },
        ],
      },
      {
        id: "vpnReady",
        questionKey: "home.guide.steps.vpnReady",
        options: [
          { id: "needProfile", labelKey: "home.guide.options.needVpnProfile", navigate: "/vpn-profiles" },
          {
            id: "haveProfile",
            labelKey: "home.guide.options.haveVpnProfile",
            navigate: "/data-sources?tab=adaptors",
          },
        ],
      },
      {
        id: "adaptorType",
        questionKey: "home.guide.steps.adaptorType",
        options: [
          { id: "db", labelKey: "home.guide.options.adaptorDb", navigate: "/data-sources?tab=adaptors" },
          { id: "api", labelKey: "home.guide.options.adaptorApi", navigate: "/data-sources?tab=adaptors" },
          { id: "other", labelKey: "home.guide.options.adaptorOther", navigate: "/data-sources?tab=adaptors" },
        ],
      },
    ],
  },
  {
    id: "excelCsv",
    icon: "upload_file",
    tone: "info",
    titleKey: "home.guide.cards.excelCsv.title",
    descriptionKey: "home.guide.cards.excelCsv.description",
    steps: [
      {
        id: "ready",
        questionKey: "home.guide.steps.fileReady",
        options: [
          { id: "ready", labelKey: "home.guide.options.fileReady", navigate: "/data-sources?tab=ingest" },
          { id: "learn", labelKey: "home.guide.options.fileLearn", navigate: "/data-sources?tab=ingest" },
        ],
      },
    ],
  },
  {
    id: "dataPrep",
    icon: "account_tree",
    tone: "secondary",
    titleKey: "home.guide.cards.dataPrep.title",
    descriptionKey: "home.guide.cards.dataPrep.description",
    steps: [
      {
        id: "prepMode",
        questionKey: "home.guide.steps.prepMode",
        options: [
          { id: "new", labelKey: "home.guide.options.prepNew", navigate: "/data-prep" },
          { id: "existing", labelKey: "home.guide.options.prepExisting", navigate: "/data-prep" },
        ],
      },
    ],
  },
  {
    id: "internalAnalysis",
    icon: "schema",
    tone: "success",
    titleKey: "home.guide.cards.internalAnalysis.title",
    descriptionKey: "home.guide.cards.internalAnalysis.description",
    steps: [
      {
        id: "analysisKind",
        questionKey: "home.guide.steps.analysisKind",
        options: [
          { id: "mapping", labelKey: "home.guide.options.mapping", navigate: "/ai-context?tab=mapping" },
          { id: "discovery", labelKey: "home.guide.options.discovery", navigate: "/adaptor-discovery" },
        ],
      },
    ],
  },
  {
    id: "guideDocs",
    icon: "menu_book",
    tone: "info",
    titleKey: "home.guide.cards.guideDocs.title",
    descriptionKey: "home.guide.cards.guideDocs.description",
    steps: [
      {
        id: "docsMode",
        questionKey: "home.guide.steps.docsMode",
        options: [
          { id: "upload", labelKey: "home.guide.options.docsUpload", navigate: "/ai-context?tab=documents" },
          { id: "browse", labelKey: "home.guide.options.docsBrowse", navigate: "/ai-context?tab=documents" },
        ],
      },
    ],
  },
  {
    id: "anomaly",
    icon: "notification_important",
    tone: "warning",
    titleKey: "home.guide.cards.anomaly.title",
    descriptionKey: "home.guide.cards.anomaly.description",
    steps: [
      {
        id: "anomalyMode",
        questionKey: "home.guide.steps.anomalyMode",
        options: [
          { id: "new", labelKey: "home.guide.options.anomalyNew", navigate: "/anomalies/new" },
          { id: "incidents", labelKey: "home.guide.options.anomalyIncidents", navigate: "/anomalies?tab=incidents" },
        ],
      },
    ],
  },
  {
    id: "insight",
    icon: "insights",
    tone: "primary",
    titleKey: "home.guide.cards.insight.title",
    descriptionKey: "home.guide.cards.insight.description",
    steps: [
      {
        id: "insightMode",
        questionKey: "home.guide.steps.insightMode",
        options: [
          { id: "jobs", labelKey: "home.guide.options.insightJobs", navigate: "/insight-report-jobs" },
          { id: "open", labelKey: "home.guide.options.insightOpen", navigate: "/insight-report-jobs" },
        ],
      },
    ],
  },
  {
    id: "chatWidget",
    icon: "widgets",
    tone: "secondary",
    titleKey: "home.guide.cards.chatWidget.title",
    descriptionKey: "home.guide.cards.chatWidget.description",
    steps: [
      {
        id: "widgetHint",
        questionKey: "home.guide.steps.widgetHint",
        options: [
          { id: "go", labelKey: "home.guide.options.openChatWidget", navigate: "/report?hint=widget" },
        ],
      },
    ],
  },
  {
    id: "chatAdaptorReport",
    icon: "chat",
    tone: "info",
    titleKey: "home.guide.cards.chatAdaptorReport.title",
    descriptionKey: "home.guide.cards.chatAdaptorReport.description",
    steps: [
      {
        id: "reportHint",
        questionKey: "home.guide.steps.reportHint",
        options: [
          { id: "go", labelKey: "home.guide.options.openChatReport", navigate: "/report?hint=adaptor" },
        ],
      },
    ],
  },
  {
    id: "dashboard",
    icon: "dashboard",
    tone: "primary",
    titleKey: "home.guide.cards.dashboard.title",
    descriptionKey: "home.guide.cards.dashboard.description",
    steps: [
      {
        id: "dashMode",
        questionKey: "home.guide.steps.dashMode",
        options: [
          { id: "list", labelKey: "home.guide.options.dashList", navigate: "/dashboards" },
          { id: "open", labelKey: "home.guide.options.dashOpen", navigate: "/dashboards" },
        ],
      },
    ],
  },
];

export function getHomeGuideCard(id: HomeGuideCardId): HomeGuideCard | undefined {
  return HOME_GUIDE_CARDS.find((c) => c.id === id);
}

/** Resolve the first step id for a card. */
export function homeGuideFirstStepId(card: HomeGuideCard): string {
  return card.steps[0]?.id ?? "";
}

export function homeGuideStep(card: HomeGuideCard, stepId: string): HomeGuideStep | undefined {
  return card.steps.find((s) => s.id === stepId);
}

/** Walk option → nextStep / navigate; used by tests and the wizard. */
export function resolveHomeGuideOption(
  card: HomeGuideCard,
  stepId: string,
  optionId: string
): { nextStep?: string; navigate?: string } | null {
  const step = homeGuideStep(card, stepId);
  const option = step?.options.find((o) => o.id === optionId);
  if (!option) return null;
  if (option.navigate) return { navigate: option.navigate };
  if (option.nextStep) return { nextStep: option.nextStep };
  return null;
}
